import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';

const OTP_VALID_SECONDS = 10 * 60;

export default function LoginModal() {
  const { isLoginModalOpen, closeLoginModal, login, verifyUnlock } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isLocked, setIsLocked]         = useState(false);
  const [otp, setOtp]                   = useState('');
  const [otpError, setOtpError]         = useState('');
  const [otpLoading, setOtpLoading]     = useState(false);
  const [otpSuccess, setOtpSuccess]     = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(OTP_VALID_SECONDS);
  const timerRef = useRef(null);

  // Reset all state when the modal closes
  useEffect(() => {
    if (!isLoginModalOpen) {
      setEmail(''); setPassword(''); setError('');
      setIsLocked(false); setOtp(''); setOtpError('');
      setOtpSuccess(false);
      clearInterval(timerRef.current);
    }
  }, [isLoginModalOpen]);

  // OTP countdown when account is locked
  useEffect(() => {
    if (isLocked && !otpSuccess) {
      setOtpSecondsLeft(OTP_VALID_SECONDS);
      timerRef.current = setInterval(() => {
        setOtpSecondsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isLocked, otpSuccess]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Completa todos los campos.'); return; }
    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);
    if (result.success) {
      closeLoginModal();
    } else if (result.locked) {
      setIsLocked(true);
    } else {
      setError(result.error || 'Credenciales inválidas.');
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError('');
    if (!otp || otp.length !== 6) { setOtpError('Ingresa el código de 6 dígitos.'); return; }
    setOtpLoading(true);
    const result = await verifyUnlock(email, otp);
    setOtpLoading(false);
    if (result.success) {
      clearInterval(timerRef.current);
      setOtpSuccess(true);
      setIsLocked(false);
    } else {
      setOtpError(result.error);
    }
  };

  if (!isLoginModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
      onClick={closeLoginModal}
    >
      <div
        className="relative w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={closeLoginModal}
          className="absolute top-4 right-4 text-[#8a8a8a] hover:text-[#d4af37] transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ── OTP unlock view ── */}
        {isLocked && (
          <>
            <h2 className="text-2xl font-bold text-[#d4af37] mb-2">Cuenta bloqueada</h2>
            <p className="text-[#b0b0b0] mb-6">
              Enviamos un código de 6 dígitos a tu correo. Ingrésalo para desbloquear tu cuenta.
            </p>
            {otpError && (
              <Alert className="mb-4 border-red-500 bg-red-500/10">
                <AlertDescription className="text-red-400">{otpError}</AlertDescription>
              </Alert>
            )}
            {otpSecondsLeft > 0 ? (
              <p className="text-sm text-[#b0b0b0] mb-4">
                Código válido por:{' '}
                <span className="text-[#d4af37] font-mono font-bold">{formatTime(otpSecondsLeft)}</span>
              </p>
            ) : (
              <p className="text-sm text-red-400 mb-4">
                El código expiró. Intenta iniciar sesión de nuevo.
              </p>
            )}
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <Label htmlFor="modal-otp" className="text-[#f5f5f0]">Código de verificación</Label>
                <Input
                  id="modal-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0] text-center tracking-[0.5em] text-xl font-mono"
                  disabled={otpSecondsLeft === 0}
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={otpLoading || otpSecondsLeft === 0}
                className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
              >
                {otpLoading ? 'Verificando…' : 'Desbloquear cuenta'}
              </Button>
            </form>
            <button
              onClick={() => { setIsLocked(false); setOtp(''); setOtpError(''); }}
              className="mt-4 text-sm text-[#b0b0b0] hover:text-[#d4af37] underline block"
            >
              ← Volver al login
            </button>
          </>
        )}

        {/* ── Success after OTP unlock ── */}
        {otpSuccess && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#d4af37] mb-2">¡Cuenta desbloqueada!</h2>
            <p className="text-[#b0b0b0] mb-6">Ya puedes iniciar sesión con tu contraseña.</p>
            <Button
              onClick={() => setOtpSuccess(false)}
              className="bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
            >
              Iniciar sesión
            </Button>
          </div>
        )}

        {/* ── Main login form ── */}
        {!isLocked && !otpSuccess && (
          <>
            <h2 className="text-2xl font-bold text-[#f5f5f0] mb-2">Iniciar sesión</h2>
            <p className="text-[#b0b0b0] mb-6">Accede a tu cuenta LXI</p>
            {error && (
              <Alert className="mb-4 border-red-500 bg-red-500/10">
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="modal-email" className="text-[#f5f5f0]">Email</Label>
                <Input
                  id="modal-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="modal-password" className="text-[#f5f5f0]">Contraseña</Label>
                <Input
                  id="modal-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
                />
              </div>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  onClick={closeLoginModal}
                  className="text-sm text-[#d4af37] hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
              >
                {isLoading ? 'Ingresando…' : 'Iniciar sesión'}
              </Button>
            </form>
            <p className="text-center text-[#b0b0b0] mt-6 text-sm">
              ¿No tienes cuenta?{' '}
              <Link
                to="/register"
                onClick={closeLoginModal}
                className="text-[#d4af37] hover:underline"
              >
                Regístrate
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
